import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, Play, Check, Plus, Users, Shield,
  CalendarDays, Wallet, Briefcase, BarChart3, ChevronRight,
  Search, Bell, Settings, LayoutDashboard, FolderKanban, FileText,
  Building2, Star, Quote
} from 'lucide-react';
import TekMark from '../components/TekMark';
import LandingNav, { useScrollToLandingHash } from '../components/LandingNav';
import LandingFooter from '../components/LandingFooter';
import SEO from '../components/SEO';
import FAQ from '../components/FAQ';
import './LandingPage.css';

// Landing-page FAQ — keyword-rich answers double as FAQPage schema for
// AI Overviews + Google rich snippets. Targets the Tier-1 keywords from
// docs/seo/SEO-STRATEGY.md.
const LANDING_FAQ = [
  {
    q: 'What is TekHub?',
    a: 'TekHub is an HRMS (HR management system) built specifically for small teams of 5 to 50 people. It combines an employee directory, leave and time-off management, payroll with payslip PDFs, project management, invoices in any currency, and financial dashboards in one calm workspace — no bolt-ons, no spreadsheets.',
  },
  {
    q: 'Is TekHub good for small businesses and startups?',
    a: 'Yes — TekHub is purpose-built for small teams. Larger HRMS platforms like BambooHR, Gusto, or Rippling target 100+ person companies and feel heavy at smaller sizes. TekHub gives founders, HR admins, and operations leads a self-serve HR system with payroll and finance baked in, set up in three minutes.',
  },
  {
    q: 'Does TekHub do payroll?',
    a: 'Yes. TekHub includes built-in payroll runs with automatic payslip PDF generation, multi-currency support, bonuses and deductions, and per-user salary history. Payslips are delivered to employees by email automatically.',
  },
  {
    q: 'Can I manage employee leave with TekHub?',
    a: 'Yes. TekHub has a complete leave management workflow: employees file requests, managers approve or reject with reasons, leave balances are tracked automatically per leave type, and the approver gets an email notification on every new request.',
  },
  {
    q: 'How much does TekHub cost?',
    a: 'TekHub is free while in beta — no credit card required, all features unlocked. Paid plans launch alongside general availability later this year with simple per-seat pricing designed for small-team budgets. See the pricing page for details.',
  },
  {
    q: 'How is TekHub different from BambooHR, Gusto, or Rippling?',
    a: 'Those platforms are excellent for 50+ employee companies but feel overbuilt at small sizes — long onboarding, sales-gated pricing, complex menus. TekHub is the small-team alternative: HR, payroll, leave, projects, and invoices in one place, self-serve setup, transparent pricing, calm UI.',
  },
  {
    q: 'Is my data secure?',
    a: 'Yes. TekHub is multi-tenant with per-organization data isolation, HTTPS-only with HSTS, role-based access controls with granular permissions, encrypted password storage (bcrypt), and weekly Postgres backups with one month of retention. We never share customer data.',
  },
  {
    q: 'How do I get started?',
    a: 'Visit tekhubb.co and click "Create your workspace." Pick a subdomain (e.g. acme.tekhubb.co), fill in your basic profile, and you have a working HRMS for your team in about three minutes. No card needed during beta.',
  },
];

// ──────────────────────────────────────────────────────────────────────
// IntersectionObserver hook for scroll-reveal
// ──────────────────────────────────────────────────────────────────────
function useReveal() {
  const root = useRef(null);
  useEffect(() => {
    const els = root.current?.querySelectorAll('.tk-reveal') || [];
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return root;
}

// Top navigation lives in src/components/LandingNav.jsx so every
// marketing page can use the same chrome. Imported above.

// ──────────────────────────────────────────────────────────────────────
// Hero
// ──────────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section className="tk-mint-plate relative pt-28 pb-24 md:pt-32 md:pb-28">
      <div className="absolute inset-0 tk-grid-bg pointer-events-none" />

      {/* botanical decoration top-right */}
      <svg
        className="absolute top-10 right-6 hidden lg:block tk-float"
        width="180" height="200" viewBox="0 0 180 200" fill="none"
        style={{ color: 'var(--mint-300)', opacity: 0.55 }}
      >
        <path d="M90 10 C90 60 90 120 90 190" stroke="currentColor" strokeWidth="1" />
        <path d="M90 40 C70 40 50 30 40 18" stroke="currentColor" strokeWidth="1" />
        <path d="M90 60 C110 60 130 50 145 38" stroke="currentColor" strokeWidth="1" />
        <path d="M90 95 C70 95 50 88 38 76" stroke="currentColor" strokeWidth="1" />
        <path d="M90 125 C110 125 132 116 148 102" stroke="currentColor" strokeWidth="1" />
        <ellipse cx="38" cy="18" rx="10" ry="4" fill="currentColor" opacity="0.6" transform="rotate(-25 38 18)"/>
        <ellipse cx="146" cy="38" rx="10" ry="4" fill="currentColor" opacity="0.6" transform="rotate(20 146 38)"/>
        <ellipse cx="36" cy="76" rx="10" ry="4" fill="currentColor" opacity="0.6" transform="rotate(-25 36 76)"/>
        <ellipse cx="150" cy="102" rx="10" ry="4" fill="currentColor" opacity="0.6" transform="rotate(20 150 102)"/>
      </svg>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 text-center">
        {/* Eyebrow */}
        <div className="tk-rise inline-flex items-center gap-2 rounded-full backdrop-blur px-3.5 py-1.5 border" style={{ background: 'var(--surface-glass)', borderColor: 'var(--surface-glass-border)', animationDelay: '0s' }}>
          <span className="w-1.5 h-1.5 rounded-full tk-pulse" style={{ background: 'var(--mint-500)' }} />
          <span className="tk-mono text-[11px]" style={{ color: 'var(--mint-700)', letterSpacing: '0.16em' }}>
            BUILDING IN PUBLIC · v0.4
          </span>
        </div>

        {/* Headline */}
        <h1
          className="tk-display tk-rise mt-7 mx-auto"
          style={{
            fontSize: 'clamp(40px, 7.4vw, 92px)',
            maxWidth: 1040,
            animationDelay: '0.08s',
          }}
        >
          One quiet workspace
          <br />
          for{' '}
          <span className="tk-italic tk-underline">
            small teams
            <svg viewBox="0 0 400 18" preserveAspectRatio="none" aria-hidden>
              <path
                d="M4 11 C 70 4, 150 16, 220 8 S 360 12, 396 6"
                stroke="currentColor"
                strokeWidth="3.2"
                fill="none"
                strokeLinecap="round"
              />
            </svg>
          </span>
        </h1>

        <p
          className="tk-rise mt-7 mx-auto text-balance"
          style={{
            fontSize: 'clamp(15px, 1.15vw, 18px)',
            color: 'var(--ink-2)',
            maxWidth: 600,
            lineHeight: 1.55,
            animationDelay: '0.18s',
          }}
        >
          People, projects, leave, payroll, and invoices — in one place,
          shaped to how a small team actually works. No bolt-ons, no add-ons,
          no spreadsheets.
        </p>

        {/* CTAs */}
        <div className="tk-rise mt-9 flex flex-wrap items-center justify-center gap-3" style={{ animationDelay: '0.28s' }}>
          <Link to="/signup" className="tk-btn tk-btn-primary">
            Create your workspace <ArrowRight size={16} />
          </Link>
          <a href="#features" className="tk-btn tk-btn-ghost">
            <Play size={14} fill="currentColor" /> See what's inside
          </a>
        </div>

        {/* tiny hint row */}
        <div className="tk-rise mt-6 flex items-center justify-center gap-5 text-[12.5px]" style={{ color: 'var(--ink-mute)', animationDelay: '0.36s' }}>
          <span className="inline-flex items-center gap-1.5"><Check size={13} style={{ color: 'var(--mint-500)' }} /> Free while in beta</span>
          <span className="inline-flex items-center gap-1.5"><Check size={13} style={{ color: 'var(--mint-500)' }} /> No card needed</span>
          <span className="inline-flex items-center gap-1.5"><Check size={13} style={{ color: 'var(--mint-500)' }} /> Self-serve setup</span>
        </div>

        {/* Dashboard mock */}
        <div className="tk-rise mt-16 md:mt-20" style={{ animationDelay: '0.5s' }}>
          <DashboardMock />
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Dashboard mock (hand-built so it actually feels like a product)
// ──────────────────────────────────────────────────────────────────────
function DashboardMock() {
  // Minto-inspired layout, repurposed for HR data so it matches what
  // the actual product does. Cards are pure-ish white on a soft paper
  // page, vibrant green pill for the primary action, big bold balances
  // with greyed-out decimals, multi-colour breakdown bars below.
  const stats = [
    { title: 'Headcount',      value: '120',     suffix: '',  delta: '+4',     deltaTone: 'up',   note: 'this month',     icon: <Users size={14} /> },
    { title: 'Active projects', value: '28',     suffix: '',  delta: '+3',     deltaTone: 'up',   note: 'this week',      icon: <FolderKanban size={14} /> },
    { title: 'Pending leave',  value: '7',       suffix: '',  delta: 'Review', deltaTone: 'flag', note: 'awaiting you',   icon: <CalendarDays size={14} /> },
    { title: 'Payroll',        value: '$128,400', suffix: '',  delta: 'May',    deltaTone: 'neutral', note: 'cycle ready', icon: <Wallet size={14} /> },
  ];

  return (
    <div
      className="tk-dash-mc tk-dash-parallax mx-auto max-w-6xl text-left"
      style={{ transform: 'perspective(1800px) rotateX(2deg)' }}
    >
      {/* top bar — softer Minto-style header instead of macOS chrome */}
      <div
        className="flex items-center justify-between px-6 py-4 border-b"
        style={{ borderColor: 'var(--surface-card-border)' }}
      >
        <div className="flex items-center gap-3">
          <TekMark size={22} word="ekHUB" />
          <span
            className="tk-mono text-[10.5px] hidden sm:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full"
            style={{ background: 'var(--mint-50)', color: 'var(--mint-700)', letterSpacing: '0.12em' }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--mint-500)' }} />
            LIVE
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px]"
            style={{ background: 'var(--paper)', border: '1px solid var(--surface-card-border)', color: 'var(--ink-mute)' }}
          >
            <Search size={12} /> Search …
            <span className="tk-mono text-[10px] ml-1">⌘K</span>
          </span>
          <span
            className="inline-flex items-center justify-center w-8 h-8 rounded-full"
            style={{ border: '1px solid var(--surface-card-border)', color: 'var(--ink-mute)' }}
          >
            <Bell size={14} />
          </span>
          <span className="w-8 h-8 rounded-full" style={{ background: 'linear-gradient(135deg, #C9D9B5, #6E9A86)' }} />
        </div>
      </div>

      {/* main panel */}
      <div className="px-6 md:px-8 py-7">
        {/* Title row + actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-7">
          <div>
            <h2 className="tk-display" style={{ fontSize: 28, lineHeight: 1.1 }}>Workspace overview</h2>
            <p className="text-[12.5px] mt-1" style={{ color: 'var(--ink-mute)' }}>
              People, projects, and payroll at a glance.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {[{ i: <Users size={13} />, t: 'Invite' }, { i: <CalendarDays size={13} />, t: 'Time off' }, { i: <ArrowUpRight size={13} />, t: 'Export' }].map((b) => (
              <span
                key={b.t}
                className="inline-flex items-center gap-1.5 text-[12.5px] px-3 py-1.5 rounded-full"
                style={{ background: 'var(--surface-card)', border: '1px solid var(--surface-card-border)', color: 'var(--ink-2)' }}
              >
                {b.i} {b.t}
              </span>
            ))}
            <span
              className="inline-flex items-center gap-1.5 text-[12.5px] px-3.5 py-1.5 rounded-full font-medium"
              style={{
                background: 'linear-gradient(105deg, #2A8E66 0%, #1F6F50 60%, #15533C 100%)',
                color: '#FFFFFF',
                boxShadow: '0 1px 0 rgba(255,255,255,0.18) inset, 0 6px 16px -8px rgba(20,80,55,0.45)',
              }}
            >
              <Plus size={13} /> Run payroll
            </span>
          </div>
        </div>

        {/* Stat cards row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5 mb-5">
          {stats.map((s) => {
            const dotted = s.value.match(/^(.+?)([.,]\d+)?$/);
            const main = dotted?.[1] ?? s.value;
            const tail = dotted?.[2] ?? '';
            return (
              <div
                key={s.title}
                className="tk-mc-card p-5 rounded-2xl"
                style={{ background: 'var(--surface-card)', border: '1px solid var(--surface-card-border)' }}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[12px]" style={{ color: 'var(--ink-mute)' }}>{s.title}</span>
                  <span
                    className="inline-flex items-center justify-center w-6 h-6 rounded-md"
                    style={{ background: 'var(--mint-50)', color: 'var(--mint-700)' }}
                  >
                    {s.icon}
                  </span>
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="tk-display" style={{ fontSize: 26, fontWeight: 600 }}>
                    {main}
                    <span style={{ color: 'var(--ink-mute)', fontWeight: 400 }}>{tail}</span>
                  </span>
                  {s.delta && (
                    <span
                      className="text-[10.5px] tk-mono px-1.5 py-0.5 rounded"
                      style={{
                        background:
                          s.deltaTone === 'up' ? 'var(--mint-50)' :
                          s.deltaTone === 'flag' ? 'rgba(229,180,80,0.18)' :
                          'var(--paper-2)',
                        color:
                          s.deltaTone === 'up' ? 'var(--mint-700)' :
                          s.deltaTone === 'flag' ? '#8C661E' :
                          'var(--ink-mute)',
                      }}
                    >
                      {s.delta}
                    </span>
                  )}
                </div>
                <div className="mt-3 text-[11.5px] flex items-center justify-between" style={{ color: 'var(--ink-mute)' }}>
                  <span>{s.note}</span>
                  <span className="inline-flex items-center gap-0.5" style={{ color: 'var(--mint-700)' }}>
                    View <ArrowRight size={11} />
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Two-up: workforce breakdown + this month */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 mb-5">
          {/* workforce breakdown */}
          <div className="tk-mc-card p-5 rounded-2xl" style={{ background: 'var(--surface-card)', border: '1px solid var(--surface-card-border)' }}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[12px]" style={{ color: 'var(--ink-mute)' }}>Workforce breakdown</div>
                <div className="mt-1 text-[13px]">
                  Headcount up <span style={{ color: 'var(--mint-700)', fontWeight: 600 }}>+4.1%</span> vs last month
                </div>
              </div>
              <span className="tk-display" style={{ fontSize: 22 }}>4 teams</span>
            </div>
            {/* multi-colour bars */}
            <div className="flex gap-1.5 h-3.5 mt-2">
              <span style={{ flex: 44, background: '#82B991', borderRadius: 999 }} />
              <span style={{ flex: 18, background: '#F0B488', borderRadius: 999 }} />
              <span style={{ flex: 28, background: '#A9C9DA', borderRadius: 999 }} />
              <span style={{ flex: 10, background: '#D6D2C4', borderRadius: 999 }} />
            </div>
            <div className="mt-4 grid grid-cols-4 gap-2">
              {[
                { color: '#82B991', icon: <Briefcase size={12} />, label: 'Eng', pct: '44%' },
                { color: '#F0B488', icon: <BarChart3 size={12} />, label: 'Product', pct: '18%' },
                { color: '#A9C9DA', icon: <Users size={12} />, label: 'Ops', pct: '28%' },
                { color: '#D6D2C4', icon: <Plus size={12} />, label: 'Other', pct: '10%' },
              ].map((b) => (
                <div key={b.label} className="flex flex-col items-center text-center">
                  <span
                    className="inline-flex items-center justify-center w-7 h-7 rounded-full mb-1.5 text-white"
                    style={{ background: b.color }}
                  >
                    {b.icon}
                  </span>
                  <span className="text-[11.5px]" style={{ color: 'var(--ink-2)' }}>{b.label}</span>
                  <span className="tk-mono text-[10px]" style={{ color: 'var(--ink-mute)' }}>{b.pct}</span>
                </div>
              ))}
            </div>
          </div>

          {/* this month + saving goal */}
          <div className="tk-mc-card p-5 rounded-2xl" style={{ background: 'var(--surface-card)', border: '1px solid var(--surface-card-border)' }}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[12px]" style={{ color: 'var(--ink-mute)' }}>Hiring this quarter</div>
                <div className="tk-display mt-1" style={{ fontSize: 26, fontWeight: 600 }}>
                  18<span style={{ color: 'var(--ink-mute)', fontWeight: 400 }}> / 24</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[11.5px] inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full"
                      style={{ background: 'var(--paper)', border: '1px solid var(--surface-card-border)', color: 'var(--ink-2)' }}>
                  <ArrowUpRight size={11} /> Share
                </span>
                <span className="text-[11.5px] inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full"
                      style={{ background: 'var(--paper)', border: '1px solid var(--surface-card-border)', color: 'var(--ink-2)' }}>
                  <Settings size={11} /> Manage
                </span>
              </div>
            </div>
            <div className="text-[11.5px] flex items-center justify-between mb-1.5" style={{ color: 'var(--ink-mute)' }}>
              <span>Goal progress</span>
              <span className="tk-mono">75%</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--mint-50)' }}>
              <div
                style={{
                  width: '75%',
                  height: '100%',
                  background: 'linear-gradient(90deg, var(--mint-500) 0%, var(--mint-700) 100%)',
                  borderRadius: 999,
                }}
              />
            </div>
            <div className="mt-4 text-[11.5px]" style={{ color: 'var(--ink-mute)' }}>
              On track to hit Q2 goal · <span style={{ color: 'var(--ink-2)', fontWeight: 500 }}>6 offers out</span>
            </div>
          </div>
        </div>

        {/* spending alerts row */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[14px] font-medium" style={{ color: 'var(--ink)' }}>Smart alerts</h3>
            <span className="text-[12px] tk-mono inline-flex items-center gap-1 px-2.5 py-1 rounded-full"
                  style={{ background: 'var(--paper)', border: '1px solid var(--surface-card-border)', color: 'var(--ink-mute)' }}>
              Sort by: <span style={{ color: 'var(--ink-2)' }}>Urgency</span>
            </span>
          </div>
          <div className="tk-mc-card rounded-2xl overflow-hidden" style={{ background: 'var(--surface-card)', border: '1px solid var(--surface-card-border)' }}>
            <div
              className="flex items-center gap-3 px-4 py-3 border-b"
              style={{ borderColor: 'var(--surface-card-border)', background: 'var(--paper)' }}
            >
              <span className="tk-mono text-[10.5px]" style={{ color: 'var(--ink-mute)', letterSpacing: '0.16em' }}>
                LEAVE REQUEST · #LR-021
              </span>
            </div>
            <div className="px-4 py-4 flex items-center gap-4">
              <span className="w-9 h-9 rounded-full inline-flex items-center justify-center text-white"
                    style={{ background: 'linear-gradient(135deg, #F0B488, #C99966)' }}>
                <CalendarDays size={16} />
              </span>
              <div className="flex-1">
                <div className="text-[13.5px]" style={{ color: 'var(--ink)', fontWeight: 500 }}>Charlotte Brooks · Annual leave</div>
                <div className="text-[11.5px]" style={{ color: 'var(--ink-mute)' }}>5 days · Jun 2 — Jun 6 · No coverage conflicts</div>
              </div>
              <div className="hidden md:grid grid-cols-3 gap-3 text-[11.5px]" style={{ color: 'var(--ink-mute)' }}>
                <div>
                  <div className="tk-mono text-[10.5px]">PRIORITY</div>
                  <div className="mt-0.5" style={{ color: 'var(--ink-2)' }}>Medium</div>
                </div>
                <div>
                  <div className="tk-mono text-[10.5px]">BALANCE</div>
                  <div className="mt-0.5" style={{ color: 'var(--ink-2)' }}>14 days</div>
                </div>
                <div>
                  <div className="tk-mono text-[10.5px]">CONFIDENCE</div>
                  <div className="mt-0.5" style={{ color: 'var(--mint-700)' }}>92%</div>
                </div>
              </div>
              <span className="inline-flex items-center gap-1 text-[12px] px-3 py-1.5 rounded-full"
                    style={{ background: 'var(--mint-700)', color: 'var(--paper)' }}>
                Approve
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Section header (eyebrow + serif headline)
// ──────────────────────────────────────────────────────────────────────
function SectionHeader({ eyebrow, line1, line2Italic, sub, align = 'center' }) {
  return (
    <div className={'tk-reveal ' + (align === 'center' ? 'text-center mx-auto' : '')} style={{ maxWidth: 720 }}>
      <div className="inline-flex items-center gap-2 mb-5">
        <span className="tk-dot" />
        <span className="tk-eyebrow">{eyebrow}</span>
      </div>
      <h2 className="tk-display" style={{ fontSize: 'clamp(32px, 4.6vw, 56px)' }}>
        {line1}{' '}
        <span className="tk-italic">{line2Italic}</span>
      </h2>
      {sub && (
        <p className="mt-4 text-[15.5px]" style={{ color: 'var(--ink-mute)', lineHeight: 1.6 }}>
          {sub}
        </p>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Features
// ──────────────────────────────────────────────────────────────────────
function Features() {
  const items = [
    {
      icn: <Users size={20} />,
      title: 'People directory',
      body: 'Every employee record, contract, role, and emergency contact in one place. Edit once, see it everywhere.',
      tone: 'mint',
      to: '/employee-management',
    },
    {
      icn: <FolderKanban size={20} />,
      title: 'Projects & threads',
      body: 'Organise work into projects with threaded conversations and contributors. Status, tags, and updates live in one calm view.',
      tone: 'paper',
    },
    {
      icn: <CalendarDays size={20} />,
      title: 'Leave management',
      body: 'Configure leave types and policies, then let the team request and approve PTO without spreadsheets.',
      tone: 'paper',
      to: '/leave-management',
    },
    {
      icn: <Wallet size={20} />,
      title: 'Payroll & salary logs',
      body: 'Track salary changes over time, run payroll cycles, and keep an auditable history of every adjustment.',
      tone: 'mint',
      to: '/payroll-software',
    },
    {
      icn: <FileText size={20} />,
      title: 'Invoices & expenses',
      body: 'Issue invoices, log expenses, and watch the totals roll up into clean monthly summaries.',
      tone: 'paper',
      to: '/invoice-management',
    },
    {
      icn: <BarChart3 size={20} />,
      title: 'Org chart & reports',
      body: 'Visualise reporting lines and pull simple reports across projects, leave, and finances when you need them.',
      tone: 'mint',
    },
  ];

  return (
    <section id="features" className="py-24 md:py-32" style={{ background: 'var(--paper)' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeader
          eyebrow="WHAT'S INSIDE"
          line1="The modules your team"
          line2Italic="actually opens."
          sub="A small set of well-built tools, not a sea of half-features. Each module is its own first-class space — sharing the same people, projects, and policies."
        />

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((it, i) => {
            // Cards with a `to` become real links to the deep-dive page;
            // crawlers + readers get the internal link, and the existing
            // hover styling still applies because the same classes ride
            // along.
            const Card = it.to ? Link : 'div';
            const cardProps = it.to
              ? { to: it.to, 'aria-label': `${it.title} — learn more` }
              : {};
            return (
              <Card
                key={i}
                {...cardProps}
                className={
                  'tk-reveal tk-lift p-7 rounded-2xl relative overflow-hidden group' +
                  (it.to ? ' tk-feature-link' : '')
                }
                style={{
                  background: it.tone === 'mint' ? 'var(--mint-50)' : 'var(--paper-2)',
                  border: '1px solid var(--paper-line)',
                  transitionDelay: `${i * 60}ms`,
                  textDecoration: 'none',
                  color: 'inherit',
                  display: 'block',
                }}
              >
                <div
                  className="inline-flex items-center justify-center w-11 h-11 rounded-xl"
                  style={{
                    background: it.tone === 'mint' ? 'var(--mint-900)' : 'var(--paper)',
                    color: it.tone === 'mint' ? 'var(--mint-200)' : 'var(--mint-700)',
                    border: '1px solid ' + (it.tone === 'mint' ? 'transparent' : 'var(--paper-line)'),
                  }}
                >
                  {it.icn}
                </div>
                <h3 className="tk-display mt-5" style={{ fontSize: 22 }}>
                  {it.title}
                </h3>
                <p className="mt-2.5 text-[14.5px]" style={{ color: 'var(--ink-2)', lineHeight: 1.6 }}>
                  {it.body}
                </p>
                {it.to && (
                  <div className="mt-5 inline-flex items-center gap-1.5 text-[13px] tk-mono" style={{ color: 'var(--mint-700)' }}>
                    LEARN MORE <ArrowUpRight size={13} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Process — Run Payroll & HR Operations / With Simple Steps
// ──────────────────────────────────────────────────────────────────────
function Process() {
  const steps = [
    {
      n: '01',
      title: 'Set up your workspace',
      body: 'Add your company details, departments, and leave policies. A short guided setup gets you to a usable workspace in a sitting.',
      panel: <SetupCompanyPanel />,
      cta: 'Start your setup',
    },
    {
      n: '02',
      title: 'Invite your people',
      body: 'Add team members one by one or paste a list of emails. Roles, departments, and salaries are set when you onboard them.',
      panel: <InvitePeoplePanel />,
      cta: 'Invite your team',
    },
    {
      n: '03',
      title: 'Run the work in one place',
      body: 'Track projects, approve leave, log salaries, send invoices — without switching tabs. The dashboard stays calm because every module follows the same shape.',
      panel: <AutopilotPanel />,
      cta: 'See it for yourself',
    },
  ];

  return (
    <section id="process" className="py-24 md:py-32" style={{ background: 'var(--paper)' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeader
          eyebrow="HOW IT WORKS"
          line1="Three steps, then"
          line2Italic="you're set."
          sub="A small workspace where setup feels like setup, not a project of its own."
        />

        <div className="mt-16 space-y-6">
          {steps.map((s, i) => (
            <div
              key={i}
              className="tk-reveal grid grid-cols-1 md:grid-cols-12 gap-6 items-center p-7 md:p-9 rounded-2xl"
              style={{
                background: i % 2 === 0 ? 'var(--paper-2)' : 'var(--mint-50)',
                border: '1px solid var(--paper-line)',
              }}
            >
              <div className={'md:col-span-5 ' + (i % 2 === 1 ? 'md:order-2' : '')}>
                <div className="flex items-center gap-3">
                  <span
                    className="tk-mono inline-flex items-center justify-center w-10 h-10 rounded-full"
                    style={{
                      background: 'var(--mint-900)',
                      color: 'var(--mint-200)',
                      fontSize: 12,
                      letterSpacing: '0.04em',
                    }}
                  >
                    {s.n}
                  </span>
                  <span className="tk-eyebrow">STEP {s.n}</span>
                </div>
                <h3 className="tk-display mt-4" style={{ fontSize: 'clamp(24px, 2.4vw, 34px)' }}>
                  {s.title}
                </h3>
                <p className="mt-3 text-[15px]" style={{ color: 'var(--ink-2)', lineHeight: 1.6 }}>
                  {s.body}
                </p>
                <Link to="/signup" className="tk-btn tk-btn-ghost tk-btn-pill-sm mt-6">
                  {s.cta} <ChevronRight size={14} />
                </Link>
              </div>
              <div className={'md:col-span-7 ' + (i % 2 === 1 ? 'md:order-1' : '')}>
                <div className="rounded-xl tk-card p-5">
                  {s.panel}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SetupCompanyPanel() {
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <span className="w-7 h-7 inline-flex items-center justify-center rounded-md" style={{ background: 'var(--mint-900)', color: 'var(--mint-200)' }}>
          <Building2 size={14} />
        </span>
        <div>
          <div className="text-[13px]">Setup your company</div>
          <div className="text-[11px]" style={{ color: 'var(--ink-mute)' }}>Step 1 of 4 · 60% complete</div>
        </div>
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-[11px] tk-mono" style={{ color: 'var(--ink-mute)' }}>COMPANY NAME *</label>
          <div className="mt-1 px-3 py-2.5 rounded-md text-[13px]" style={{ background: 'var(--paper-2)', border: '1px solid var(--paper-line)' }}>
            Northwind Studio
          </div>
        </div>
        <div>
          <label className="text-[11px] tk-mono" style={{ color: 'var(--ink-mute)' }}>WHAT KIND OF COMPANY ARE YOU?</label>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {['Technology', 'Media', 'Education', 'Legal Enterprise', 'Retail Services', 'Industrial', 'Others'].map((t, i) => (
              <span
                key={t}
                className="text-[11.5px] px-2.5 py-1 rounded-full inline-flex items-center gap-1"
                style={{
                  background: i === 0 || i === 4 ? 'var(--mint-900)' : 'var(--paper-2)',
                  color: i === 0 || i === 4 ? 'var(--paper)' : 'var(--ink-2)',
                  border: i === 0 || i === 4 ? '1px solid transparent' : '1px solid var(--paper-line)',
                }}
              >
                {(i === 0 || i === 4) && <Check size={10} />} {t}
              </span>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[11px] tk-mono" style={{ color: 'var(--ink-mute)' }}>HOW LARGE IS YOUR COMPANY?</label>
          <div className="mt-2 relative">
            <div className="h-1 rounded-full" style={{ background: 'var(--paper-2)', border: '1px solid var(--paper-line)' }} />
            <div className="absolute top-0 left-0 h-1 rounded-full" style={{ width: '38%', background: 'var(--mint-500)' }} />
            <div className="absolute" style={{ left: '38%', top: '-6px', transform: 'translateX(-50%)' }}>
              <span className="block w-3.5 h-3.5 rounded-full" style={{ background: 'var(--mint-900)', border: '2px solid var(--paper)' }} />
            </div>
          </div>
          <div className="flex justify-between mt-2 text-[10.5px] tk-mono" style={{ color: 'var(--ink-mute)' }}>
            <span>1</span><span>50</span><span>200</span><span>500</span><span>1k+</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function InvitePeoplePanel() {
  const rows = [
    { n: 'Ava Morgan', r: 'Lead UX/UI Designer', s: 'pending' },
    { n: 'Charlotte Brooks', r: 'Senior Engineer', s: 'sent' },
    { n: 'Henry Sufford', r: 'Product Designer', s: 'sent' },
    { n: 'Mariana Pen', r: 'Data Analyst', s: 'pending' },
  ];
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[13px]">Invite people</div>
          <div className="text-[11px]" style={{ color: 'var(--ink-mute)' }}>4 invites queued · 2 sent today</div>
        </div>
        <span className="tk-btn tk-btn-primary tk-btn-pill-sm" style={{ padding: '6px 12px' }}>
          <Plus size={12} /> Add row
        </span>
      </div>
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--paper-line)' }}>
        <div className="grid grid-cols-12 px-3 py-2 text-[10.5px] tk-mono" style={{ background: 'var(--paper-2)', color: 'var(--ink-mute)' }}>
          <span className="col-span-5">EMPLOYEE</span>
          <span className="col-span-5">ROLE</span>
          <span className="col-span-2 text-right">STATUS</span>
        </div>
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-12 px-3 py-2.5 items-center text-[12.5px] border-t" style={{ borderColor: 'var(--paper-line)' }}>
            <span className="col-span-5 inline-flex items-center gap-2">
              <span className="w-6 h-6 rounded-full inline-block" style={{ background: ['#C9D9B5', '#F2D7A8', '#B6CCC3', '#D9C0DC'][i % 4] }} />
              {r.n}
            </span>
            <span className="col-span-5" style={{ color: 'var(--ink-2)' }}>{r.r}</span>
            <span className="col-span-2 text-right">
              <span
                className="tk-mono text-[10px] px-1.5 py-0.5 rounded"
                style={{
                  background: r.s === 'sent' ? 'var(--mint-100)' : 'var(--paper-2)',
                  color: r.s === 'sent' ? 'var(--mint-700)' : 'var(--ink-mute)',
                  border: r.s === 'sent' ? 'none' : '1px solid var(--paper-line)',
                }}
              >
                {r.s.toUpperCase()}
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AutopilotPanel() {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[13px]">Recent activity</div>
          <div className="text-[11px]" style={{ color: 'var(--ink-mute)' }}>Across the workspace today</div>
        </div>
        <span className="tk-mono text-[10.5px] inline-flex items-center gap-1.5" style={{ color: 'var(--mint-700)' }}>
          <span className="w-1.5 h-1.5 rounded-full tk-pulse" style={{ background: 'var(--mint-500)' }} />
          LIVE
        </span>
      </div>
      <ul className="space-y-2.5">
        {[
          { i: <Wallet size={14} />, t: 'May salary log added', sub: '$28,500 across 65 employees', tag: 'PAYROLL' },
          { i: <CalendarDays size={14} />, t: '3 leave requests approved', sub: 'Charlotte, Henry, Marie · this week', tag: 'LEAVE' },
          { i: <Briefcase size={14} />, t: 'Project thread updated', sub: 'Mobile App · backend dev · 4 contributors', tag: 'PROJECTS' },
          { i: <FileText size={14} />, t: 'Invoice INV-0042 issued', sub: 'Northwind Studio · $12,400', tag: 'INVOICE' },
        ].map((row, i) => (
          <li
            key={i}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl"
            style={{ background: 'var(--paper-2)', border: '1px solid var(--paper-line)' }}
          >
            <span
              className="inline-flex items-center justify-center w-8 h-8 rounded-lg"
              style={{ background: 'var(--mint-900)', color: 'var(--mint-200)' }}
            >
              {row.i}
            </span>
            <div className="flex-1">
              <div className="text-[13px]">{row.t}</div>
              <div className="text-[11px]" style={{ color: 'var(--ink-mute)' }}>{row.sub}</div>
            </div>
            <span
              className="tk-mono text-[10px] px-1.5 py-0.5 rounded"
              style={{
                background: 'var(--mint-100)',
                color: 'var(--mint-700)',
              }}
            >
              {row.tag}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Principles — replaces the fabricated testimonials block
// ──────────────────────────────────────────────────────────────────────
function Principles() {
  const items = [
    {
      n: '01',
      title: 'One workspace, not a suite',
      body: 'Modules share the same people, projects, and policies. Nothing to integrate, nothing to keep in sync.',
    },
    {
      n: '02',
      title: 'Calm by default',
      body: 'Quiet colours, generous spacing, and only the controls you need on screen at once. The opposite of dashboard-as-cockpit.',
    },
    {
      n: '03',
      title: 'Honest about scope',
      body: 'Built for small teams under one roof. No marketplace, no third-party integrations — yet. We ship what we use.',
    },
  ];
  return (
    <section id="principles" className="py-24 md:py-32" style={{ background: 'var(--paper-2)', borderTop: '1px solid var(--paper-line)', borderBottom: '1px solid var(--paper-line)' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeader
          eyebrow="WHAT WE BELIEVE"
          line1="A small product, made"
          line2Italic="on purpose."
          sub="A few principles we keep returning to. They shape every screen, every default, every shipping choice."
        />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {items.map((p, i) => (
            <div
              key={p.n}
              className="tk-reveal tk-lift p-7 rounded-2xl"
              style={{
                background: i === 1 ? 'var(--mint-50)' : 'var(--paper)',
                border: '1px solid var(--paper-line)',
                transitionDelay: `${i * 80}ms`,
              }}
            >
              <span
                className="tk-mono inline-flex items-center justify-center w-10 h-10 rounded-full"
                style={{
                  background: 'var(--mint-900)',
                  color: 'var(--mint-200)',
                  fontSize: 12,
                  letterSpacing: '0.04em',
                }}
              >
                {p.n}
              </span>
              <h3 className="tk-display mt-5" style={{ fontSize: 22 }}>{p.title}</h3>
              <p className="mt-2.5 text-[14.5px]" style={{ color: 'var(--ink-2)', lineHeight: 1.6 }}>
                {p.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────
// CTA
// ──────────────────────────────────────────────────────────────────────
function CTA() {
  return (
    <section id="pricing" className="py-24 md:py-28" style={{ background: 'var(--paper)' }}>
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div
          className="tk-reveal relative rounded-3xl p-10 md:p-16 text-center overflow-hidden"
          style={{
            background: 'var(--mint-900)',
            color: 'var(--paper)',
          }}
        >
          {/* decorative botanical */}
          <svg
            className="absolute -top-10 -right-10 hidden md:block"
            width="280" height="280" viewBox="0 0 200 200" fill="none"
            style={{ color: 'rgba(173, 210, 180, 0.18)' }}
          >
            <circle cx="100" cy="100" r="98" stroke="currentColor" strokeWidth="0.6" />
            <circle cx="100" cy="100" r="78" stroke="currentColor" strokeWidth="0.6" />
            <circle cx="100" cy="100" r="58" stroke="currentColor" strokeWidth="0.6" />
            <circle cx="100" cy="100" r="38" stroke="currentColor" strokeWidth="0.6" />
            <path d="M100 0 V200" stroke="currentColor" strokeWidth="0.6" />
            <path d="M0 100 H200" stroke="currentColor" strokeWidth="0.6" />
            <path d="M30 30 L170 170 M170 30 L30 170" stroke="currentColor" strokeWidth="0.6" />
          </svg>

          <div className="relative">
            <div className="inline-flex items-center gap-2 mb-5">
              <span className="w-1.5 h-1.5 rounded-full tk-pulse" style={{ background: 'var(--mint-300)' }} />
              <span className="tk-mono text-[11px]" style={{ color: 'var(--mint-200)', letterSpacing: '0.2em' }}>
                FREE WHILE IN BETA
              </span>
            </div>
            <h3 className="tk-display mx-auto" style={{ fontSize: 'clamp(34px, 5vw, 64px)', maxWidth: 800, lineHeight: 1.02 }}>
              A workspace your team{' '}
              <span className="tk-italic" style={{ color: 'var(--mint-200)' }}>actually opens.</span>
            </h3>
            <p className="mt-5 mx-auto" style={{ color: 'rgba(244,239,230,0.7)', maxWidth: 540, fontSize: 15.5 }}>
              Spin up your own workspace in a few minutes. Free while we're in beta — no card, no commitment.
            </p>

            <form
              className="mt-9 mx-auto flex flex-col sm:flex-row gap-2 max-w-md"
              onSubmit={(e) => { e.preventDefault(); window.location.href = '/signup'; }}
            >
              <input
                type="email"
                placeholder="you@company.com"
                className="tk-input flex-1"
                style={{ background: 'rgba(244,239,230,0.08)', borderColor: 'rgba(244,239,230,0.2)', color: 'var(--paper)' }}
              />
              <button
                type="submit"
                className="tk-btn"
                style={{
                  background: 'var(--paper)',
                  color: 'var(--mint-900)',
                  border: 'none',
                }}
              >
                Get started <ArrowRight size={15} />
              </button>
            </form>
            <div className="mt-5 text-[12px]" style={{ color: 'rgba(244,239,230,0.55)' }}>
              Self-serve · Multi-tenant · Cancel anytime
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Compare strip — quiet editorial line that surfaces the four /vs/*
// pages from the homepage. These are SEO-valuable destinations (people
// search "TekHub vs BambooHR" etc.), and the strip is the lightest way
// to expose them without adding a whole comparison section.
// ──────────────────────────────────────────────────────────────────────
function CompareStrip() {
  const peers = [
    { name: 'BambooHR',    to: '/vs/bamboohr' },
    { name: 'Gusto',       to: '/vs/gusto' },
    { name: 'Rippling',    to: '/vs/rippling' },
    { name: 'Zoho People', to: '/vs/zoho-people' },
  ];
  return (
    <section className="tk-compare-strip" style={{ background: 'var(--paper-2)', borderTop: '1px solid var(--paper-line)', borderBottom: '1px solid var(--paper-line)' }}>
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-14 md:py-16 text-center">
        <div className="inline-flex items-center gap-2 mb-4">
          <span className="tk-dot" />
          <span className="tk-eyebrow">COMPARE</span>
        </div>
        <p className="tk-display mx-auto" style={{ fontSize: 'clamp(22px, 2.4vw, 30px)', lineHeight: 1.35, maxWidth: 760 }}>
          Switching from something bigger?{' '}
          <span className="tk-italic" style={{ color: 'var(--ink-mute)' }}>See how TekHub compares to</span>{' '}
          {peers.map((p, i) => (
            <React.Fragment key={p.name}>
              <Link to={p.to} className="tk-compare-link">{p.name}</Link>
              {i < peers.length - 2 ? ', ' : i === peers.length - 2 ? ', or ' : '.'}
            </React.Fragment>
          ))}
        </p>
      </div>
    </section>
  );
}

// Footer lives in src/components/LandingFooter.jsx so every
// marketing page uses the same chrome.

// ──────────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const root = useReveal();
  // When arriving from a /#section link on another page (e.g. clicking
  // "Features" in the nav from /pricing), scroll the section into view.
  useScrollToLandingHash();
  return (
    <>
      <SEO
        title="One quiet workspace for small teams"
        description="TekHub puts people, projects, leave, and payroll in one place — shaped to how a small team actually works. No bolt-ons, no add-ons, no spreadsheets."
        path="/"
      />
      <main ref={root} className="tk-land">
        <LandingNav />
        <Hero />
        <Features />
        <Process />
        <Principles />
        <CompareStrip />
        <FAQ
          eyebrow="FREQUENTLY ASKED"
          line1="Everything you wanted"
          line2Italic="to ask first."
          items={LANDING_FAQ}
        />
        <CTA />
        <LandingFooter />
      </main>
    </>
  );
}
